import {
    placesAPIPresent,
    getAddressObjectFromDescription,
    addGooglePlacesAPI,
    getNonShippedStates,
} from './'

import {
    isSameAddress,
    addressHasRequiredFields,
} from '@/helpers'

import { log } from '@/services/Log'

export const getPlacesPredictions = async (input) => {
    //  There are two API sources for verification
    //  if no address is found we show the no results page,
    //  if we found but none is equal to the one the user provided, then we show suggestions,
    //  if we found at least one matching the user input we assume its correct and continue
    log('getPlacesPredictions START', { ...input })
    const nonShippedStates = getNonShippedStates();
    const addressInDeliverableState = address=> !nonShippedStates.includes(address.stateAcronym)

    const invalidResult = (errorMessage) => ({
        success: false, predictions: [], addressPredictions: [], errorMessage,
    })
    if (!placesAPIPresent()) {
        try {
            await addGooglePlacesAPI()
        } catch {
            return invalidResult('maps API failed to load script')
        }
    }
    if (!input.street2) {
        // Checking if line 2 has been added as part of line 1
        const unitNumberPattern = /(bsmt|basement)$|(((APT|APARTMENT|UNIT|#|SUITE)*)([.\s]*)((number|num|#|n)*)([\s]*))([0-9/]+)$/gim
        const matches = unitNumberPattern.exec(input.street1)
        if (matches !== null && matches.length) {
            const split = `${input.street1}`.split(unitNumberPattern)
            input.street1 = `${(split?.[0] || input.street1)}`.trim()
            input.street2 = `${matches?.[0] || input.street2}`.trim()
        }
    }
    const inputString = [input.street1, input.city, input.stateAcronym, input.zip].reduce(
        (fullAddress, addrChunk) => `${fullAddress}${addrChunk ? `${fullAddress ? ', ' : ''}${addrChunk}` : ''}`,
    )
    // Concat Address into a string
    if (!inputString || !placesAPIPresent()) {
        return invalidResult('Invalid input')
    }
    const { success, validAddress, predictions, addressPredictions } = (
        await new Promise((resolve) => (new window.google.maps.places.AutocompleteService()).getPlacePredictions(
            { input: inputString, types: ['address'], componentRestrictions: { country: ['us'] } },
            async (predictions) => {
                const buildAddr = (address) => ({ ...input, ...address, street2: input.street2 ? input.street2 : address.street2 })
                // Fetching Suggestions for the input address
                const inputPredictionsFromPlacesGetPlace = await getAddressObjectFromDescription({ description: inputString })
                const tempArray = inputPredictionsFromPlacesGetPlace.map(buildAddr)
                let addressPredictions = predictions ?
                    // Places query insertion, some addresses are valid but don't return predictions
                    ([...predictions]
                        .filter(({ types }) => types?.includes('street_address'))
                        .map(async ({ description }) => {
                            const descriptionPredictedAddressReturned = await getAddressObjectFromDescription({ description, isAddressValid: true })
                            descriptionPredictedAddressReturned.forEach((address) => tempArray.push(buildAddr(address)))
                            return null
                        }))
                    :
                    []
                addressPredictions = await Promise.all(addressPredictions)
                addressPredictions = tempArray
                addressPredictions = addressPredictions.filter(
                    (addressA, index) => ( // Remove duplicates from places query insertion
                        index === addressPredictions.findIndex((addressB) => JSON.stringify(addressB) === JSON.stringify(addressA))
                    ),
                )
                addressPredictions = addressPredictions.filter(addressHasRequiredFields)
                addressPredictions = addressPredictions.filter(addressInDeliverableState)

                const validAddress = (
                    // Has a prediction with a perfect match
                    !!addressPredictions.find((addr) => (
                        isSameAddress(addr, input)
                    ))
                )
                return resolve({
                    success: addressPredictions.length > 0,
                    validAddress,
                    predictions,
                    addressPredictions,
                })
            },
        ))
    )
    log('getPlacesPredictions END', { success, validAddress, predictions, addressPredictions })
    return { success, validAddress, predictions, addressPredictions }
}
